import cookie from '../utils/cookies';

/**
 * The root element that contains all potentially targeted elements for this
 * module.
 *
 * @type {HTMLElement}
 */
const $root         = document.querySelector( '.property-index' );
const siteContainer = document.querySelector( '.site-container' );

/**
 * The cookie name for the saved property data.
 *
 * @type {string}
 */
const WISHLIST_KEY = 'pickard_properties_wishlist_ids';

/**
 * The amount of days until the cookie will expire.
 *
 * @type {number}
 */
const WISHLIST_EXPIRATION_DAYS = 31;


/**
 * The key used for referencing the user's last search.
 *
 * @type {string}
 */
const QUERY_STORAGE_KEY = 'pickard_properties_search_query';

/**
 * Keys that are valid in the current search.
 *
 * @type {string[]}
 */
const VALID_QUERY_KEYS = [
	'property-let-type',
	'property-area',
	'property-university',
	'property-bedroom',
	'property-bathrooms',
	'property-toilets',
	'property-status',
	'property-house-shares',
	'property-all-inclusive',
	'property-keywords',
	'property-sort',
];

/**
 * A list of elements that are needed for this module.
 *
 * @type {{
 *     sortFieldHeader: HTMLElement,
 *     sortFieldFilters: HTMLElement,
 *     filtersForm: HTMLElement,
 *     properties: NodeListOf<HTMLElement>,
 *     propertyCount: HTMLElement,
 *     propertyTitlesText: HTMLElement,
 *     propertySearchReturn: HTMLElement,
 * }}
 */
const $elements = {
	propertySearchReturn: document.querySelector('[data-property-search-return]'),
};

/**
 * Sets up the $elements object.
 *
 * Called once the $root element has been confirmed to exist.
 *
 * @return void
 */
function setupElements() {
	$elements.sortFieldHeader      = $root.querySelector( '#property-sort-search-header' );
	$elements.sortFieldFilters     = $root.querySelector( '#property-sort-primary' );
	$elements.filtersForm          = $root.querySelector( 'form.property-sidebar__wrap' );
	$elements.properties           = $root.querySelectorAll( '.property-card' );
	$elements.propertyCount        = $root.querySelector( '.property-index__titles--count' );
	$elements.propertyTitlesText   = $root.querySelector( '.property-index__titles--text' );
}

/**
 * Modifies the output of the sort dropdown in the filters to match the element
 * in the header, once it's been updated, and then submits the form.
 *
 * @param event
 *
 * @return void
 */
function matchSortFields( event ) {
	if ( event.target !== $elements.sortFieldHeader ) {
		return;
	}

	$elements.sortFieldFilters.value = event.target.value;
	$elements.filtersForm.submit();

	if ( siteContainer ) {
		siteContainer.classList.add('site-container--loading');
	}

}

/**
 * Update the user's cookie to potentially save or un-save a property in their
 * wishlist.
 *
 * @param {HTMLElement} $property
 *
 * @return void
 */
function togglePropertySaveState( $property ) {
	if ( ! $property.dataset.id ) {
		return;
	}

	const id = parseInt( $property.dataset.id );

	let wishlist = localStorage.getItem( WISHLIST_KEY );

	if ( wishlist === null ) {
		wishlist = [];
	} else {
		wishlist = JSON.parse( wishlist )
			.map( ( value ) => parseInt( value ) )
			.filter( ( value ) => value > 0 );
	}

	let values = ! wishlist.includes( id ) ?
		[ ...wishlist, id ] :
		wishlist.filter( ( value ) => value !== id );

	localStorage.setItem( WISHLIST_KEY, JSON.stringify( values ) );

	if ( $root.classList.contains( 'property-index--wishlist' ) ) {
		$elements.propertyCount.innerHTML = values.length;
		if ( 1 === values.length ) {
			$elements.propertyTitlesText.innerHTML = 'property';
		} else {
			$elements.propertyTitlesText.innerHTML = 'properties';
		}

		if ( $property.classList.contains( 'property-card--saved' ) ) {
			$property.remove();
		}
	}

	$property.classList.toggle( 'property-card--saved' );
}

/**
 * Potentially handle multiple events when a user clicks on a property.
 *
 * @param {Event} event
 *
 * @return bool
 */
function handlePropertyClicks( event ) {
	const $target = event.target;
	let $property = $target;

	if ( ! $target.classList.contains( 'property-card' ) ) {
		$property = $target.closest( '.property-card' );
	}

	if ( $target.classList.contains( 'property-card__bookmark' ) ) {
		event.preventDefault();
		event.stopPropagation();

		togglePropertySaveState( $property );
	}
}

/**
 * When the user loads the relevant page, update their local storage to contain
 * a value that allows us to reference the search, per user.
 *
 * @return void
 */
function updateUserSearchRef() {
	if (! window?.ajaxSettings) {
		return;
	}

	const url = new URL(window.location.href);

	if (! url.search) {
		return;
	}

	const value = [ ...url.searchParams ].reduce(
		(accumulator, [ key, value ]) => {
			if (VALID_QUERY_KEYS.includes(key) && !! value) {
				accumulator[key] = value;
			}

			return accumulator;
		},
		{}
	);

	window.localStorage.setItem(QUERY_STORAGE_KEY, window.btoa(JSON.stringify(value)));
}

/**
 * Attempt to acquire the user's last property search from their local storage.
 *
 * Returns false if we weren't able to acquire anything.
 *
 * @return void|false
 */
function getUserSearchQuery() {
	const query = window.localStorage.getItem(QUERY_STORAGE_KEY);

	if (! query) {
		return false;
	}

	let value = window.atob(query);

	try {
		value = JSON.parse(value);
	} catch {
		return false;
	}

	return value;
}

/**
 * If the button exists, update the "href" attribute with the relevant queries from
 * the locally stored filters.
 *
 * @return void
 */
function maybeUpdateSearchReturnLink($button) {
	const base = $button.dataset.propertySearchReturn;

	if (! base || ! $button.href) {
		return;
	}

	const query = getUserSearchQuery();

	if (! query) {
		return;
	}

	const url = new URL(base);

	Object.entries(query).forEach(([key, value]) => {
		url.searchParams.set(key, value);
	});

	$button.setAttribute('href', url);
}

/**
 * Finds any wishlisted properties and adds the appropriate class to them
 *
 * @return void
 */
export function maybeMarkSavedProperties() {
	let wishlist = localStorage.getItem( WISHLIST_KEY );

	if ( ! wishlist || ! wishlist.length ) {
		return;
	}

	wishlist = JSON.parse( wishlist );

	$elements.properties.forEach( ( $card ) => {
		const id = parseInt( $card.dataset.id ?? 0 );

		if ( wishlist.includes( id ) ) {
			$card.classList.add( 'property-card--saved' );
		}
	} );
}

/**
 * Constructor.
 *
 * @return void
 */
export default function propertySearch() {
	if ( $elements.propertySearchReturn ) {
		maybeUpdateSearchReturnLink($elements.propertySearchReturn);
	}

	if ( ! $root ) {
		return;
	}

	setupElements();
	updateUserSearchRef();
	maybeMarkSavedProperties();

	if ( $elements.sortFieldHeader && $elements.sortFieldFilters && $elements.filtersForm ) {
		$elements.sortFieldHeader.addEventListener( 'change', matchSortFields );
	}

	if ( $elements.properties && $elements.propertyCount && $elements.propertyTitlesText ) {
		$elements.properties.forEach(
			( $property ) => $property.addEventListener( 'click', handlePropertyClicks )
		);
	}

}
